import { useEffect, useState } from 'react';
import Tooltip from '../../Tooltip';
import styles from '../GirderConnection.module.css';

const DEFAULTS = {
    emitDebounceSeconds: 1,
    minSyncIntervalSeconds: 10,
    pollIntervalSeconds: 600,
};

function resolveAutoPull(autoPull) {
    const raw = autoPull || {};
    return {
        enabled: !!raw.enabled,
        emitDebounceSeconds: raw.emitDebounceSeconds ?? DEFAULTS.emitDebounceSeconds,
        minSyncIntervalSeconds:
            raw.minSyncIntervalSeconds
            ?? raw.pullDebounceSeconds
            ?? DEFAULTS.minSyncIntervalSeconds,
        pollIntervalSeconds: raw.pollIntervalSeconds ?? DEFAULTS.pollIntervalSeconds,
    };
}

function AutoSyncSettingsDialog({ open, onClose, current, pending, enabled, onSave }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className={styles.autoSyncDialogOverlay}
            onClick={onClose}
            role="presentation"
        >
            <div
                className={styles.autoSyncDialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="auto-sync-settings-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.autoSyncDialogHeader}>
                    <h3 id="auto-sync-settings-title" className={styles.autoSyncDialogTitle}>
                        Sync settings
                    </h3>
                    <button
                        type="button"
                        className={styles.autoSyncDialogClose}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className={styles.autoSyncDialogBody}>
                    <div className={styles.autoSyncDialogField}>
                        <p className={styles.autoSyncDialogCopy}>
                            We always notify connected nodes when our data changes, after a short
                            delay so that many updates in a row are lumped together. Configure the
                            length of the delay here (default: 1s). After this hub syncs from a spoke,
                            it notifies other spokes using each spoke&apos;s view of the data.
                        </p>
                        <span className={styles.autoSyncFieldControl}>
                            <input
                                type="number"
                                min={0}
                                className={styles.autoPullInput}
                                key={`emit-${current.emitDebounceSeconds}`}
                                defaultValue={current.emitDebounceSeconds}
                                disabled={pending}
                                onBlur={(e) => onSave({
                                    emitDebounceSeconds: Number(e.target.value) || 0,
                                })}
                            />
                            <span className={styles.autoSyncUnit}>s</span>
                        </span>
                    </div>

                    <div className={styles.autoSyncDialogField}>
                        <p className={styles.autoSyncDialogCopy}>
                            When auto-sync is enabled, we can limit how often syncing happens by
                            setting the minimum interval between sync operations. This helps avoid
                            unnecessary processing. Configure the minimum interval here
                            (default: 10s)
                        </p>
                        <span className={styles.autoSyncFieldControl}>
                            <input
                                type="number"
                                min={0}
                                className={styles.autoPullInput}
                                key={`min-${current.minSyncIntervalSeconds}`}
                                defaultValue={current.minSyncIntervalSeconds}
                                disabled={pending || !enabled}
                                onBlur={(e) => onSave({
                                    minSyncIntervalSeconds: Number(e.target.value) || 0,
                                })}
                            />
                            <span className={styles.autoSyncUnit}>s</span>
                        </span>
                    </div>

                    <div className={styles.autoSyncDialogField}>
                        <p className={styles.autoSyncDialogCopy}>
                            When auto-sync is enabled, periodically check for updates in case a
                            notification from the other node was missed. Configure the polling
                            interval here (default: 600s)
                        </p>
                        <span className={styles.autoSyncFieldControl}>
                            <input
                                type="number"
                                min={30}
                                className={styles.autoPullInput}
                                key={`poll-${current.pollIntervalSeconds}`}
                                defaultValue={current.pollIntervalSeconds}
                                disabled={pending || !enabled}
                                onBlur={(e) => onSave({
                                    pollIntervalSeconds:
                                        Number(e.target.value) || DEFAULTS.pollIntervalSeconds,
                                })}
                            />
                            <span className={styles.autoSyncUnit}>s</span>
                        </span>
                    </div>
                </div>
                <div className={styles.autoSyncDialogFooter}>
                    <button
                        type="button"
                        className={styles.autoSyncDialogDone}
                        onClick={onClose}
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Primary auto-sync control: status-styled toggle + settings gear.
 */
export default function AutoSyncSettings({
    connection,
    updateAutoPull,
}) {
    const current = resolveAutoPull(connection.autoPull);
    const pending = !!(
        updateAutoPull?.isPending
        && updateAutoPull.variables?.connectionId === connection._id
    );
    const [settingsOpen, setSettingsOpen] = useState(false);

    const save = (patch) => {
        const next = { ...current, ...patch };
        updateAutoPull.mutate({
            connectionId: connection._id,
            autoPull: {
                enabled: !!next.enabled,
                emitDebounceSeconds: Number(next.emitDebounceSeconds) || 0,
                minSyncIntervalSeconds: Number(next.minSyncIntervalSeconds) || 0,
                pollIntervalSeconds: Number(next.pollIntervalSeconds) || DEFAULTS.pollIntervalSeconds,
            },
        });
    };

    return (
        <div className={styles.autoSyncPrimary} data-sourcepanel="AutoSyncSettings.js">
            <div className={styles.autoSyncControlRow}>
                <span className={styles.autoSyncTitle}>Auto-sync</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={current.enabled}
                    aria-label={current.enabled ? 'Auto-sync on' : 'Auto-sync off'}
                    className={`${styles.autoSyncToggle} ${current.enabled ? styles.autoSyncToggleOn : styles.autoSyncToggleOff}`}
                    disabled={pending}
                    onClick={() => save({ enabled: !current.enabled })}
                >
                    <span className={styles.autoSyncToggleKnob} />
                    <span className={styles.autoSyncToggleLabel}>
                        {current.enabled ? 'On' : 'Off'}
                    </span>
                </button>
                <Tooltip text="Sync settings" position="top">
                    <button
                        type="button"
                        className={styles.autoSyncGearButton}
                        aria-label="Sync settings"
                        onClick={() => setSettingsOpen(true)}
                    >
                        ⚙
                    </button>
                </Tooltip>
            </div>
            <p className={styles.autoSyncHint}>
                Eligible hub links always notify peers when our published data changes. Auto-sync on means we also pull automatically when notified (or on the poll interval).
            </p>

            {updateAutoPull?.isError
                && updateAutoPull.variables?.connectionId === connection._id && (
                <p className={styles.autoSyncError} role="alert">
                    {updateAutoPull.error?.message}
                </p>
            )}

            <AutoSyncSettingsDialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                current={current}
                pending={pending}
                enabled={current.enabled}
                onSave={save}
            />
        </div>
    );
}

export { resolveAutoPull, DEFAULTS as AUTO_SYNC_DEFAULTS };
